import { useNavigate, useSearch } from '@tanstack/react-router';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { Heading } from '@components/ui/typography/heading';
import { Text } from '@components/ui/typography/text';
import { Flex, VStack } from '@components/ui/layout/flex';

import { renewResponseToken, useBranding } from '@utils/api';

type ResponseTokenRenewalProps = {
  responseId?: string;
};

const ResponseTokenRenewal: FC<ResponseTokenRenewalProps> = ({ responseId }) => {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [renewed, setRenewed] = useState(false);
  const token = search.token as string;

  const renewToken = () => {
    setLoading(true);
    renewResponseToken(responseId, { Authorization: `Bearer ${token}` })
      .then(() => setRenewed(true))
      .catch(() => {
        toast.error(t('form.tokenExpired.errors.title'), {
          description: t('form.tokenExpired.errors.renewalFailed'),
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Flex align="center" justify="center" className="w-full min-h-[100svh] p-4">
      <VStack className="w-full max-w-sm bg-white p-6 rounded-2xl text-center" gap={0}>
        <img
          className="p-1 w-full h-[100px] object-contain"
          alt={`${branding?.name}'s Logo`}
          src={branding?.logo}
        />
        <Separator aria-hidden="true" className="my-4" />
        {!renewed ? (
          <>
            <Heading level={4} className="mb-4 text-center">
              {t('form.tokenExpired.title')}
            </Heading>
            <Button
              style={{
                backgroundColor: branding?.buttonColor,
                color: branding?.fontColor,
              }}
              className="w-full"
              onClick={renewToken}
              loading={loading}
            >
              {t('form.tokenExpired.buttons.newLink')}
            </Button>
            <Text className="text-gray-500 mt-2">{t('form.tokenExpired.newLinkMessage')}</Text>
            <div className="relative w-full text-center my-4">
              <div className="border-b border-gray-300 w-full absolute top-1/2" />
              <Text
                as="span"
                className="bg-white text-gray-500 px-2 relative"
              >
                {t('form.tokenExpired.divider')}
              </Text>
            </div>
            <Button
              style={{
                backgroundColor: branding?.buttonColor,
                color: branding?.fontColor,
              }}
              className="w-full"
              onClick={() => navigate({ to: '/secure/login' })}
            >
              {t('form.tokenExpired.buttons.login')}
            </Button>
            <Text className="text-gray-500 mt-2">{t('form.tokenExpired.patientLoginMessage')}</Text>
          </>
        ) : (
          <>
            <Heading level={4} className="mb-4 text-center">
              {t('form.tokenExpired.linkSentTitle')}
            </Heading>
            <Text className="text-gray-500">{t('form.tokenExpired.linkSentMessage')}</Text>
          </>
        )}
      </VStack>
    </Flex>
  );
};

export default ResponseTokenRenewal;
